import React from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import {makeStyles, Box, Paper, Typography, LinearProgress, Link} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import {useI18N} from "../../../common/common";


const useStyles = makeStyles((theme) => ({
    paper: {
        boxShadow: "0px 6px 10px rgba(0, 0, 0, 0.14), 0px 1px 18px rgba(0, 0, 0, 0.12), 0px 3px 5px rgba(0, 0, 0, 0.2)",
        borderRadius: theme.spacing(0.75),
        padding: theme.spacing(2, 0),
        [theme.breakpoints.only("xs")]: {
            paddingBottom: theme.spacing(0),
        },
    },
    progressLabel: {
        textAlign: "center",
        margin: theme.spacing(1, 3),
        fontWeight: 300,
        [theme.breakpoints.only("xs")]: {
            marginTop: theme.spacing(0),
            marginBottom: theme.spacing(1.5)
        },
    },
    linearProgress: {
        height: "5px",
        background: theme.palette.secondary["50"],
        [theme.breakpoints.up("sm")]: {
            height: "9px",
        },
        [theme.breakpoints.up("md")]: {
            marginRight: theme.spacing(1.5)
        },
        "& .MuiLinearProgress-bar": {
            borderTopRightRadius: theme.spacing(0.5),
            borderBottomRightRadius: theme.spacing(0.5)
        }
    },

    step: {
        margin: theme.spacing(1, 3),
        marginBottom: theme.spacing(2),
    },
    stepCompleted: {
        fontWeight: "400",
        fontSize: theme.spacing(2),
        lineHeight: "140%",
        textDecoration: "line-through"
    },
    stepUncompleted: {
        color: theme.palette.primary[900],
        fontWeight: "600",
        lineHeight: "125%",
        letterSpacing: "0.15px"
    },
    stepLink: {
        color: theme.palette.primary[900],
        "&:hover": {
            textDecorationLine: "none",
            cursor: "pointer"
        }
    },
    dot: {
        width: "24px",
        height: "24px",
        fontWeight: "600",
        paddingLeft: "1px",
        display: "inline-block",
        marginRight: theme.spacing(1.5),
        borderRadius: "50%",
        border: "1.5px solid " + theme.palette.primary[900],
        color: theme.palette.primary[900],
        textAlign: "center"
    }
}));

const ReservationProgress = ({daysRemaining, steps, children, className}) => {

    const classes = useStyles();
    const {i18n} = useI18N();

    const calculateProgress = (daysRemaining) => {
        let res = null;
        if(daysRemaining < 0) {
            res = 0;
        }else if(daysRemaining === 0 ) {
            res = 100;
        } else if(daysRemaining > 0 && daysRemaining <= 3 ) {
            res = 90;
        } else if(daysRemaining > 3 && daysRemaining <= 6) {
            res = 85;
        } else if(daysRemaining > 6 && daysRemaining <= 12 ) {
            res = 75;
        } else if(daysRemaining > 12 && daysRemaining < 30 ) {
            res = 60;
        } else {
            res = 50;
        }
        return res;
    };

    const createBoldFont = (str) => {
        return (
            <span
                key={str}
                style={{fontWeight: "bold"}}
            >
                {str}
            </span>
        );
    };

    return (
        <Paper className={clsx(classes.paper, className)}>
            <Typography
                variant="h5"
                className={classes.progressLabel}
            >
                {typeof(daysRemaining) === "number" ? (
                    <>
                        {daysRemaining ? (
                            <>{i18n("view.reservations.your_holiday_starts_in", {numOfDays: daysRemaining, boldFont: createBoldFont})}</>
                        ) : (
                            <>{i18n("view.reservations.your_holiday_starts_today", {boldFont: createBoldFont})}</>
                        )}
                        {" 🎉"}
                    </>
                ) : (
                    <Skeleton/>
                )}
            </Typography>
            {typeof(daysRemaining) === "number" ? (
                <LinearProgress
                    variant="determinate"
                    value={calculateProgress(daysRemaining)}
                    className={classes.linearProgress}
                />
            ) : (
                <Skeleton/>
            )}
            {steps && 
            <Box
                pt={4.125}
                pb={2}
            >
                {steps.map((step, idx) => {
                    return (
                        <Typography
                            key={idx}
                            className={clsx(classes.step, step?.target ? classes.stepUncompleted : classes.stepCompleted)}
                            component="div"
                        >
                            <span
                                className={classes.dot}
                            >
                                {idx + 1}
                            </span>
                            {step?.target ?
                                (
                                    <Link
                                        className={classes.stepLink}
                                        href={step.target}
                                    >{step.text}</Link>
                                ) : step.text}
                        </Typography>
                    );
                })}
            </Box>}
            <>
                {children}
            </>
        </Paper>
    );
};

ReservationProgress.propTypes = {
    className: PropTypes.string,
    daysRemaining: PropTypes.number,
    steps: PropTypes.array,
    children: PropTypes.any
};

export default ReservationProgress;
